/** Объект журнала, живых обновлений и чата: проект. */
export const PROJECT_ENTITY = 'project';

/**
 * Проект (спецификация подпроекта 5, разделы 4 и 5). Вид: под ключ, почасовой, подписка; сделка
 * «дизайн и маркетинг» ведётся как проект под ключ.
 */
export const PROJECT_KINDS = ['fixed', 'tm', 'subscription'] as const;
export type ProjectKind = (typeof PROJECT_KINDS)[number];

/**
 * Состояние: `planning` — собран, идёт назначение; `active` — в работе; `paused` — на паузе;
 * `delivering` — итоговый акт у клиента (5.3); `done` — сдан или закрыт.
 */
export const PROJECT_STATUSES = ['planning', 'active', 'paused', 'delivering', 'done'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

/** Срочность (дополнения владельца): «Отложенный», «Оптимальный», «Срочный». */
export const URGENCIES = ['deferred', 'optimal', 'urgent'] as const;
export type Urgency = (typeof URGENCIES)[number];

/** Риск проекта (дополнения владельца): «Всё продумано», «Есть концепция», «Только идея». */
export const PROJECT_RISKS = ['clear', 'concept', 'idea'] as const;
export type ProjectRisk = (typeof PROJECT_RISKS)[number];

export const DEFAULT_URGENCY: Urgency = 'optimal';
export const DEFAULT_PROJECT_RISK: ProjectRisk = 'concept';

export const PROJECT_TITLE_MAX = 120;
export const STAGE_TITLE_MAX = 120;

/** Вид работ сделки (модуль `sales`): `fixed`, `tm`, `subscription`, `design_marketing`. */
export type DealWorkKind = 'fixed' | 'tm' | 'subscription' | 'design_marketing';

/** Вид проекта по виду работ сделки; без вида — под ключ. */
export function projectKindOf(workKind: DealWorkKind | null): ProjectKind {
  return workKind === 'tm' || workKind === 'subscription' ? workKind : 'fixed';
}

/**
 * Ручные переходы состояния руководителем: `planning → active` («Начать работу»), `active ↔ paused`.
 * Остальные состояния меняет сдача проекта (5.3), их вручную не выставить.
 */
export const PROJECT_ACTIONS = ['start', 'pause', 'resume'] as const;
export type ProjectAction = (typeof PROJECT_ACTIONS)[number];

const TRANSITIONS: Readonly<Record<ProjectStatus, Partial<Record<ProjectAction, ProjectStatus>>>> = {
  planning: { start: 'active' },
  active: { pause: 'paused' },
  paused: { resume: 'active' },
  delivering: {},
  done: {},
};

/** Состояние после действия; `null` — из этого состояния действие недоступно. */
export function projectAfter(status: ProjectStatus, action: ProjectAction): ProjectStatus | null {
  return TRANSITIONS[status][action] ?? null;
}

/**
 * Проект удаляется, только если у него нет подтверждённых часов и подписанных документов самого проекта
 * (итоговый акт, допсоглашения; заявка принадлежит сделке и не мешает); иначе — только архив.
 */
export function canDeleteProject(input: {
  readonly hasConfirmedHours: boolean;
  readonly hasSignedDocuments: boolean;
}): boolean {
  return !input.hasConfirmedHours && !input.hasSignedDocuments;
}

/** Название без лишних пробелов; пустое или длиннее `max` — `null`. */
export function normalizeTitle(value: string, max: number): string | null {
  const title = value.trim().replace(/\s+/g, ' ');
  return title === '' || title.length > max ? null : title;
}
